/*
 * Copyright 1999-2024 Jagex Ltd.
 */
import React, { FunctionComponent } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { googleConsentModeScript } from '@components/head/meta/MetaHelmet/scripts';
// Images for meta tags
import rsLogo114 from '@static/logos/runescape-114.png';
import rsLogo120 from '@static/logos/runescape-120.png';
import rsLogo144 from '@static/logos/runescape-144.png';
import rsLogo152 from '@static/logos/runescape-152.png';
import rsLogo16 from '@static/logos/runescape-16.png';
import rsLogo180 from '@static/logos/runescape-180.png';
import rsLogo192 from '@static/logos/runescape-192.png';
import rsLogo32 from '@static/logos/runescape-32.png';
import rsLogo57 from '@static/logos/runescape-57.png';
import rsLogo60 from '@static/logos/runescape-60.png';
import rsLogo72 from '@static/logos/runescape-72.png';
import rsLogo76 from '@static/logos/runescape-76.png';
import rsLogo96 from '@static/logos/runescape-96.png';
import fbShareJpg from '@static/social-images/social-share-fb.jpg';

type MetaHelmetProps = {
  url?: string;
};

export const MetaHelmet: FunctionComponent<MetaHelmetProps> = ({ url = window.location.href }) => {
  const { t } = useTranslation();

  const pageTitle = t('linkedAccounts:meta:title');
  const siteTitle = t('general:meta:siteTitle');
  const description = t('linkedAccounts:meta:siteDescription');
  const title = `${pageTitle} - ${siteTitle}`;

  return (
    <Helmet>
      {/* Title and standard meta information */}
      <meta charSet="UTF-8" />
      <title>{title}</title>
      <meta name="viewport" content="width=device-width, initial-scale=1" />
      <meta name="description" content={description} />
      <meta name="author" content="Jagex" />
      <meta httpEquiv="X-UA-Compatible" content="IE=edge" />
      <link rel="dns-prefetch" href="//www.google-analytics.com" />
      <link rel="dns-prefetch" href="//ajax.googleapis.com" />
      <link rel="dns-prefetch" href="//connect.facebook.net" />
      <meta name="format-detection" content="telephone=no" />
      <meta name="theme-color" content="#1C2C34" />

      {/* Cookiebot script */}
      <script data-cookieconsent="ignore">{googleConsentModeScript}</script>
      <script
        id="Cookiebot"
        src="https://consent.cookiebot.com/uc.js"
        data-cbid="9147fcf2-a466-4a5d-82ab-43e528833d80"
        data-blockingmode="auto"
      />

      {/* Apple information */}
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />

      {/* Favicon and other icons */}
      <link rel="icon" type="image/png" sizes="192x192" href={rsLogo192} />
      <link rel="icon" type="image/png" sizes="32x32" href={rsLogo32} />
      <link rel="icon" type="image/png" sizes="96x96" href={rsLogo96} />
      <link rel="icon" type="image/png" sizes="16x16" href={rsLogo16} />

      {/* Apple icons */}
      <link rel="apple-touch-icon" sizes="57x57" href={rsLogo57} />
      <link rel="apple-touch-icon" sizes="60x60" href={rsLogo60} />
      <link rel="apple-touch-icon" sizes="72x72" href={rsLogo72} />
      <link rel="apple-touch-icon" sizes="76x76" href={rsLogo76} />
      <link rel="apple-touch-icon" sizes="114x114" href={rsLogo114} />
      <link rel="apple-touch-icon" sizes="120x120" href={rsLogo120} />
      <link rel="apple-touch-icon" sizes="144x144" href={rsLogo144} />
      <link rel="apple-touch-icon" sizes="152x152" href={rsLogo152} />
      <link rel="apple-touch-icon" sizes="180x180" href={rsLogo180} />

      {/* Microsoft information */}
      <meta name="msapplication-navbutton-color" content="#1C2C34" />
      <meta name="msapplication-TileColor" content="#ffffff" />
      <meta name="msapplication-TileImage" content={rsLogo144} />

      {/* Open Graph/Facebook information */}
      <meta property="og:title" content={title} />
      <meta property="og:site_name" content="RuneScape" />
      <meta property="og:image" content={fbShareJpg} />
      <meta property="og:url" content={url} />
      <meta property="og:description" content={description} />

      {/* Twitter information */}
      <meta name="twitter:domain" content="RuneScape.com" />
      <meta name="twitter:url" content={url} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image:src" content={fbShareJpg} />
      <meta name="twitter:site" content="@RuneScape" />
    </Helmet>
  );
};
